import { executeWithSpecialistReport } from '../specialist-report';
/**
 * Tool Registry
 *
 * What a session offers the model and runs. The tools are this editor's
 * (the plugin registry, filled by a loader); which of them are offered, and
 * what the model reads about each, is the editor's kits (`kits.ts`).
 */

import type { ToolDefinition, ToolSpec } from './types';
import { ObjectRefError, type ToolContext } from '../agent-helpers';
import { describeTool, kitsVersion, offeredToolNames } from '../kits';

export type { ToolDefinition, ToolSpec } from './types';

// NOTE: Callers must import a plugin loader before using this module.
// - Full app: import '../../plugins/loader' (this editor's tools + inference plugins)
// - Headless MCP server, bench, tests: import '../ai/mcp-loader' (no inference plugins, nor the
//   tools that need the editor's sign-in); the live MCP server registers EDITOR_TOOLS itself

import { getPluginByToolName, registryVersion } from '../../plugins/registry';

let _cachedVersion = '';
let _toolDefs: ToolSpec[] = [];

/** Changes whenever what getToolDefs() answers may: the kits, or a tool (re)registered. */
function toolsVersion(): string {
    return `${kitsVersion()}:${registryVersion()}`;
}

/** The tools the session offers, described, in kit order. Rebuilds when the kits or the tools change. */
export function getToolDefs(): ToolSpec[] {
    const v = toolsVersion();
    if (v !== _cachedVersion) {
        _toolDefs = offeredToolNames().flatMap(name => {
            const def = getPluginByToolName(name)?.tool;
            return def ? [describeTool(def.tool)] : [];
        });
        _cachedVersion = v;
    }
    return _toolDefs;
}

/** The definitions of the tools the session offers, in kit order. */
export function offeredToolDefinitions(): ToolDefinition[] {
    return offeredToolNames().flatMap(name => {
        const def = getPluginByToolName(name)?.tool;
        return def ? [def] : [];
    });
}

/** Look up a full ToolDefinition by name. */
export function getToolDefinition(toolName: string): ToolDefinition | undefined {
    return getPluginByToolName(toolName)?.tool;
}

/** Execute a tool by name. */
export async function executeTool(
    toolName: string,
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const def = getToolDefinition(toolName);
    if (!def) return `Unknown tool: ${toolName}`;
    try {
        return await (def.specialist
            ? executeWithSpecialistReport(input, args => def.execute(args, ctx))
            : def.execute(input, ctx));
    } catch (error) {
        // An ambiguous or disposed object reference is the model's to fix: answer
        // with the candidate paths instead of failing the call.
        if (error instanceof ObjectRefError) return `Error: ${error.message}`;
        throw error;
    }
}

export type { ToolContext } from '../agent-helpers';
export { isToolErrorResult, TOOL_ERROR_PREFIXES } from '../tool-errors';
export { getPluginByToolName };
